import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/GlobalAPI';

const InfluencerPaymentCallback = () => {
  const [status, setStatus] = useState('Checking...');
  const [displayStatus, setDisplayStatus] = useState('Checking payment status...');
  const [merchantOrderId, setMerchantOrderId] = useState(null);
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(true);

  // Helper function to format status for display
  const formatStatus = (statusValue) => {
    if (!statusValue) return 'Unknown';
    
    const statusUpper = String(statusValue).toUpperCase();
    
    if (statusUpper === 'PENDING') {
      return 'We are confirming your payment with your bank. Please do not refresh.';
    }
    if (statusUpper === 'PAYMENT_SUCCESS' || statusUpper === 'COMPLETED') {
      return 'Payment Successful! ✅';
    }
    if (statusUpper === 'PAYMENT_ERROR' || statusUpper === 'FAILED' || statusUpper === 'PAYMENT_DECLINED') {
      return 'Payment Failed ❌';
    }
    
    return statusValue;
  };

  // Check payment status
  const checkStatus = async (orderId) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/phonepe/order-status/${orderId}`);
      const statusValue = resp.data?.state || resp.data?.status;
      
      setStatus(statusValue || 'UNKNOWN');
      setDisplayStatus(formatStatus(statusValue));
      
      // Stop polling if payment is completed or failed
      const statusUpper = String(statusValue || '').toUpperCase();
      if (statusUpper === 'PAYMENT_SUCCESS' || statusUpper === 'COMPLETED' || 
          statusUpper === 'PAYMENT_ERROR' || statusUpper === 'FAILED' || statusUpper === 'PAYMENT_DECLINED') {
        setIsPending(false);
        setError(null);
        
        // Clear localStorage on success/failure
        try {
          window.localStorage.removeItem('merchantOrderId');
        } catch (e) {}
      } else {
        setIsPending(true);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to fetch order status');
      setStatus('Error');
      setDisplayStatus('Error checking payment status');
      setIsPending(false);
    }
  };

  useEffect(() => {
    // Try to read merchantOrderId from query params or localStorage
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('merchantOrderId') || params.get('orderId') || window.localStorage.getItem('merchantOrderId');
    
    if (!orderId) {
      setStatus('No merchantOrderId found.');
      setDisplayStatus('No order ID found. Please contact support.');
      setIsPending(false);
      return;
    }
    
    // If PhonePe redirected with orderId, persist it as merchantOrderId for subsequent checks
    if (params.get('orderId') && !window.localStorage.getItem('merchantOrderId')) {
      try { 
        window.localStorage.setItem('merchantOrderId', params.get('orderId')); 
      } catch (e) {}
    }
    
    setMerchantOrderId(orderId);

    // Initial check
    checkStatus(orderId);
  }, []); // Run once on mount

  // Separate effect for auto-refresh when pending
  useEffect(() => {
    if (!merchantOrderId || !isPending) return;

    // Set up auto-refresh every 5 seconds if still pending
    const intervalId = setInterval(() => {
      checkStatus(merchantOrderId);
    }, 5000); // 5 seconds

    // Cleanup interval on unmount or when not pending
    return () => {
      clearInterval(intervalId);
    };
  }, [merchantOrderId, isPending]);

  const getStatusColor = () => {
    const statusUpper = String(status).toUpperCase();
    if (statusUpper === 'PAYMENT_SUCCESS' || statusUpper === 'COMPLETED') {
      return 'text-green-600';
    }
    if (statusUpper === 'PAYMENT_ERROR' || statusUpper === 'FAILED' || statusUpper === 'PAYMENT_DECLINED') {
      return 'text-red-600';
    }
    if (statusUpper === 'PENDING') {
      return 'text-yellow-600';
    }
    return 'text-gray-600';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Payment Status</h3>
        
        {merchantOrderId && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Order ID:</p>
            <p className="text-sm font-mono font-medium text-gray-700 break-all">{merchantOrderId}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center justify-center mb-3">
            {isPending && (
              <svg className="animate-spin h-8 w-8 text-yellow-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <p className={`text-xl font-semibold ${getStatusColor()}`}>
              {displayStatus}
            </p>
          </div>
          
          {isPending && (
            <p className="text-sm text-gray-500 text-center mt-2">
              ⏳ Automatically checking... Please wait
            </p>
          )}
          
          {error && (
            <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {!isPending && (
            <a 
              href="/influencer" 
              className="px-4 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-center font-medium transition-colors"
            >
              Back to Influencer
            </a>
          )}
          
          <button
            className="px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium transition-colors"
            onClick={async () => {
              if (!merchantOrderId) return;
              setDisplayStatus('Checking...');
              setIsPending(true);
              setError(null);
              await checkStatus(merchantOrderId);
            }}
            disabled={isPending}
          >
            {isPending ? 'Checking...' : 'Refresh Status'}
          </button>
        </div>

        {isPending && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              💡 Our system is automatically verifying your payment. This page will update automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfluencerPaymentCallback;
